import { Link } from "react-router-dom";
import { ArrowRight, Check, Zap, Music2, Sparkles, Play, Star, Quote } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1453090927415-5f45085b65c0?auto=format&fit=crop&w=2400&q=80";
const CLEAN_IMG = "https://images.unsplash.com/photo-1517409091671-180985f2ca15?auto=format&fit=crop&w=1600&q=80";
const MESSY_IMG = "https://images.pexels.com/photos/11404176/pexels-photo-11404176.jpeg?auto=compress&cs=tinysrgb&w=1600";
const CHURCH_IMG = "https://images.pexels.com/photos/34516689/pexels-photo-34516689.jpeg?auto=compress&cs=tinysrgb&w=2000";

export default function Landing() {
  return (
    <div data-testid="landing-page" className="bg-[#050505] text-white">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden grain-overlay">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Atmospheric stage with microphone"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        </div>

        <div className="relative px-6 md:px-12 lg:px-24 pb-24 md:pb-32 pt-40 w-full">
          <div className="max-w-4xl">
            <p className="eyebrow mb-6 fade-up text-zinc-400">Stageletics presents</p>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-semibold leading-[0.95] tracking-tighter fade-up text-white">
              AuraSleeve
              <br />
              <span className="text-[#E8A851]">Cover the chaos.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed fade-up">
              The Aura Sleeve quietly covers every cord running down your
              microphone stands — from wired mics to personal monitors and
              tablets. One sleeve. A sleeker stage.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 fade-up">
              <Link
                to="/product"
                data-testid="hero-shop-now-button"
                className="btn-primary"
              >
                Shop Aura Sleeve <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#problem" className="btn-secondary !text-white !border-white/40 hover:!bg-white/10" data-testid="hero-learn-more-button">
                See the difference
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM / SOLUTION BENTO */}
      <section id="problem" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-3xl mb-16">
          <p className="eyebrow mb-4">The Problem</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight">
            Cords are killing your stage.
          </h2>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
            Every Sunday, your team builds something beautiful. Then a tangled
            mess of XLR cables, IEM lines, and ME-1 wires drag every eye away
            from worship and onto the chaos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 relative overflow-hidden border border-white/10 group h-[480px]">
            <img
              src={MESSY_IMG}
              alt="Messy cables on stage"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 md:p-12">
              <p className="eyebrow mb-3 text-zinc-400">Before</p>
              <h3 className="font-display text-3xl md:text-4xl font-medium text-white">
                Visible. Distracting. Unprofessional.
              </h3>
            </div>
          </div>

          <div className="border border-white/10 p-8 md:p-12 bg-white/[0.02] flex flex-col justify-between min-h-[480px]">
            <div>
              <p className="eyebrow mb-3 text-[#E8A851]">The Sleeve</p>
              <h3 className="font-display text-2xl md:text-3xl font-medium leading-tight">
                Cover the chaos in seconds.
              </h3>
              <p className="mt-5 text-zinc-400 leading-relaxed">
                Aura Sleeve slides over your stand, hiding XLR, ME-1, IEM, and
                power cords behind a clean matte surface.
              </p>
            </div>
            <ul className="mt-8 space-y-3">
              {[
                "Fits standard mic stands",
                "Hides multiple cords at once",
                "Matte black, stage-ready finish",
                "Few-minute install — no tools",
              ].map((feat) => (
                <li key={feat} className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-[#E8A851] shrink-0" />
                  <span className="text-zinc-300">{feat}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="border border-white/10 p-8 bg-white/[0.02]">
            <Music2 className="w-6 h-6 text-[#E8A851] mb-5" />
            <h4 className="font-display text-xl font-medium mb-2">
              Built for worship
            </h4>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Designed for the unique demands of live church production and
              touring stages.
            </p>
          </div>
          <div className="border border-white/10 p-8 bg-white/[0.02]">
            <Zap className="w-6 h-6 text-[#E8A851] mb-5" />
            <h4 className="font-display text-xl font-medium mb-2">
              Install in a few minutes
            </h4>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Slide it on. Cinch it tight. You're done before soundcheck ends.
            </p>
          </div>
          <div className="border border-white/10 p-8 bg-white/[0.02]">
            <Sparkles className="w-6 h-6 text-[#E8A851] mb-5" />
            <h4 className="font-display text-xl font-medium mb-2">
              Premium materials
            </h4>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Rigid, lightweight, and built to last. A non-reflective matte
              finish absorbs stage lighting instead of bouncing it back.
            </p>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER VIDEO */}
      <BeforeAfterVideo />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* CHURCH CONTEXT */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src={CHURCH_IMG}
          alt="Church worship service"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
        <div className="absolute inset-0 flex items-center px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <p className="eyebrow mb-4 text-zinc-400">Made for</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight tracking-tight text-white">
              The teams behind
              <br />
              every Sunday morning.
            </h2>
            <p className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
              From small chapels to mega-church mainstages, Aura Sleeve gives
              your AV team one less thing to apologize for.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
            <img
              src={CLEAN_IMG}
              alt="Clean microphone stand"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-6 left-6 px-3 py-1 bg-black/70 backdrop-blur-md border border-white/30">
              <p className="text-xs uppercase tracking-[0.2em] text-white">After</p>
            </div>
          </div>
          <div>
            <p className="eyebrow mb-4">Pricing</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight">
              One stand or the whole stage —
              <br />
              we've got you.
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              Start with a single sleeve, or grab the 3-pack and outfit your
              entire front line for less.
            </p>

            <div className="mt-10 space-y-3">
              <div className="border border-white/10 p-6 flex items-center justify-between hover:bg-white/[0.03] transition-colors">
                <div>
                  <p className="text-sm uppercase tracking-[0.15em] text-zinc-400 mb-1">
                    Single
                  </p>
                  <p className="font-display text-2xl font-medium">
                    Aura Sleeve
                  </p>
                </div>
                <p className="font-display text-3xl font-semibold">$59</p>
              </div>

              <div className="relative border border-[#E8A851] p-6 flex items-center justify-between bg-gradient-to-br from-[#E8A851]/10 to-transparent">
                <div className="absolute -top-3 left-6 bg-[#E8A851] text-black text-xs font-semibold px-3 py-1 uppercase tracking-wider">
                  Best Value
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.15em] text-[#E8A851] mb-1">
                    Bundle of 3
                  </p>
                  <p className="font-display text-2xl font-medium">
                    Save 10%
                  </p>
                </div>
                <p className="font-display text-3xl font-semibold">$159</p>
              </div>
            </div>

            <Link
              to="/product"
              data-testid="pricing-cta-button"
              className="btn-primary mt-10 inline-flex"
            >
              Order Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ===========================================================
   Before / After Video Block
   ---
   Pick ONE of the modes below by setting VIDEO_SOURCE:

   1) Placeholder (default):   VIDEO_SOURCE = ""
   2) Self-hosted MP4:         VIDEO_SOURCE = "/aura-before-after.mp4"
                               (drop file in /app/frontend/public/)
   3) YouTube:                 VIDEO_SOURCE = "https://www.youtube.com/watch?v=XXXX"
                               or "https://youtu.be/XXXX"
   4) Vimeo:                   VIDEO_SOURCE = "https://vimeo.com/123456789"

   Platform is auto-detected. Optional poster image at
   /app/frontend/public/aura-before-after-poster.jpg (mp4 mode only).
   =========================================================== */
const VIDEO_SOURCE = ""; // ← Paste your video URL or path here when ready
const POSTER_SRC = "/aura-before-after-poster.jpg";

function detectVideoType(src) {
  if (!src) return { type: "placeholder" };
  const s = src.trim();
  // YouTube
  const yt = s.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
  );
  if (yt) {
    return {
      type: "youtube",
      embedUrl: `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`,
    };
  }
  // Vimeo
  const vm = s.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) {
    return {
      type: "vimeo",
      embedUrl: `https://player.vimeo.com/video/${vm[1]}?title=0&byline=0&portrait=0`,
    };
  }
  // Default: treat as MP4 file path/URL
  return { type: "mp4", src: s };
}

function BeforeAfterVideo() {
  const video = detectVideoType(VIDEO_SOURCE);

  return (
    <section
      data-testid="before-after-video-section"
      className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32"
    >
      <div className="max-w-3xl mb-12">
        <p className="eyebrow mb-4">See it in action</p>
        <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight">
          Before &amp; After.
          <br />
          <span className="text-[#E8A851]">One sleeve. Instant difference.</span>
        </h2>
        <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
          Watch a typical worship stage transform — from a tangle of XLR and
          ME-1 cables to a clean, professional front line.
        </p>
      </div>

      <div
        data-testid="video-frame"
        className="relative w-full aspect-video border border-white/10 overflow-hidden bg-[#0F0F13]"
      >
        {video.type === "mp4" && (
          <video
            data-testid="before-after-video-mp4"
            className="absolute inset-0 w-full h-full object-cover"
            controls
            playsInline
            preload="metadata"
            poster={POSTER_SRC}
          >
            <source src={video.src} type="video/mp4" />
          </video>
        )}

        {(video.type === "youtube" || video.type === "vimeo") && (
          <iframe
            data-testid={`before-after-video-${video.type}`}
            src={video.embedUrl}
            title="Aura Sleeve Before & After"
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        )}

        {video.type === "placeholder" && (
          <div
            data-testid="video-placeholder"
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8A851]/10 via-transparent to-white/[0.03]" />
            <div className="absolute inset-0 grain-overlay opacity-60" />

            <div className="relative z-10 flex flex-col items-center max-w-xl">
              <div className="w-20 h-20 rounded-full border border-white/20 flex items-center justify-center mb-6 backdrop-blur-sm bg-white/[0.03]">
                <Play className="w-8 h-8 text-white pl-1" fill="currentColor" />
              </div>
              <p className="eyebrow mb-3 text-[#E8A851]">Video coming soon</p>
              <h3 className="font-display text-2xl md:text-3xl font-medium leading-tight">
                Before / After demo
              </h3>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                A short film showing a typical stage transform with the Aura
                Sleeve. Shot on real church and venue stages.
              </p>

              <div className="mt-10 grid grid-cols-2 gap-3 w-full max-w-md">
                <div className="border border-white/10 bg-black/40 p-5 backdrop-blur-sm">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                    Before
                  </p>
                  <p className="font-display text-base text-zinc-300">
                    Tangled cords
                  </p>
                </div>
                <div className="border border-[#E8A851]/40 bg-gradient-to-br from-[#E8A851]/15 to-transparent p-5 backdrop-blur-sm">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#E8A851] mb-2">
                    After
                  </p>
                  <p className="font-display text-base text-white">
                    Clean &amp; sleek
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


/* ===========================================================
   Testimonials — placeholder quotes. Edit copy inline.
   =========================================================== */
const TESTIMONIALS = [
  {
    quote:
      "First Sunday we ran three Aura Sleeves across the front line — nobody noticed the cables and everybody noticed the stage. That's exactly the reaction we wanted.",
    name: "Marcus Chen",
    role: "Production Director, Grace City Church",
    location: "Austin, TX",
  },
  {
    quote:
      "We used to gaffer-tape XLRs down every week. Now install takes minutes and the stage photographs 10× better. Worth every dollar.",
    name: "Priya Alvarez",
    role: "AV Lead, Bethel Community",
    location: "Nashville, TN",
  },
  {
    quote:
      "I run four stands with IEM plus wired mic runs. Aura hides everything in one clean sleeve. Zero snags, zero re-taping. It just works.",
    name: "Jordan Reilly",
    role: "Touring FOH Engineer",
    location: "Los Angeles, CA",
  },
];

function Testimonials() {
  return (
    <section
      data-testid="testimonials-section"
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-3xl mb-14">
        <p className="eyebrow mb-4">Loved by AV teams</p>
        <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight">
          The teams already covering the chaos.
        </h2>
        <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
          Church production leads, worship pastors, and touring engineers using
          Aura Sleeve every Sunday.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TESTIMONIALS.map((t, i) => (
          <figure
            key={t.name}
            data-testid={`testimonial-card-${i}`}
            className="relative border border-white/10 bg-white/[0.02] p-8 flex flex-col justify-between min-h-[320px] hover:border-[#E8A851]/40 transition-colors"
          >
            <Quote className="w-6 h-6 text-[#E8A851]/60 mb-6" />
            <blockquote className="text-zinc-200 leading-relaxed text-[15px]">
              &ldquo;{t.quote}&rdquo;
            </blockquote>
            <figcaption className="mt-8 pt-6 border-t border-white/10">
              <div className="flex items-center gap-0.5 mb-3">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star
                    key={s}
                    className="w-3.5 h-3.5 text-[#E8A851]"
                    fill="currentColor"
                  />
                ))}
              </div>
              <p className="font-display text-base text-white">{t.name}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{t.role}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{t.location}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
